<template>
  <div
    :class="[
      'activity-cv-block activity-cv-block--last',
      { 'activity-cv-block--visible': isSkillsVisible },
    ]"
  >
    <el-divider></el-divider>

    <div class="activity__subtitle">
      Skills
    </div>

    <div class="activity-cv-block__visibility-toggler">
      <el-button type="primary" @click="toggleSkillsVisibility">{{
        isSkillsVisible ? 'Hide skills' : 'Show skills'
      }}</el-button>
    </div>

    <div class="activity-cv-block__content" v-if="isSkillsVisible">
      <el-form
        class="activity-cv-block__item-form"
        label-position="top"
        v-for="(skill, index) in skillArray"
        :key="index"
        :rules="skillFormRules"
        :model="skill"
        ref="skill"
      >
        <div class="row">
          <div class="col col-xs-12 col-md-6">
            <el-form-item
              label="Type"
              prop="skillTypeId"
              :error="skill.skillTypeIdError"
            >
              <el-select
                class="wide"
                v-model="skill.skillTypeId"
                placeholder="Choose from the list"
              >
                <el-option label="Other" :value="0" />
                <el-option
                  v-for="type in skillTypeDescriptions"
                  :key="type.value"
                  :label="type.description"
                  :value="type.value"
                />
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12 col-md-6">
            <el-form-item label="Level" prop="level" :error="skill.levelError">
              <el-select
                class="wide"
                v-model="skill.level"
                placeholder="Choose from the list"
              >
                <el-option
                  v-for="level in descriptions.skillLevel"
                  :key="level.value"
                  :label="level.description"
                  :value="level.value"
                />
              </el-select>
            </el-form-item>
          </div>

          <div class="col col-xs-12">
            <el-form-item
              label="Other skill type"
              prop="otherSkillType"
              :error="skill.otherSkillTypeError"
            >
              <el-input type="primary" v-model="skill.otherSkillType"></el-input>
            </el-form-item>
          </div>
        </div>

        <div class="activity-cv-block__buttons">
          <confirmation-dialog
            content="Are you sure to delete this skill?"
            @on-confirm="deleteSkill(skill)"
          >
            <el-button
              class="activity-cv-block__remove-button"
              type="primary"
              :disabled="isButtonDisabled('delete-skill')"
            >
            <el-icon><Delete/></el-icon>
            Delete
            </el-button>
          </confirmation-dialog>
          <el-button
            type="primary"
            @click="validateSkill(skill, index)"
            :disabled="isButtonDisabled('save-skill')"
          >
            Save skill
          </el-button>
        </div>
      </el-form>

      <el-button type="primary" @click="addSkill">
        <el-icon><Plus/></el-icon>
        Add skill
      </el-button>
    </div>
  </div>
</template>

<script>
import {
  getSkillTypeDescriptions,
  addSkill,
  updateSkill,
  removeSkill,
} from '@/api/skill.js';

import { showMessage } from '@/utils/messages.js';
import {
  parseErrorsByFields,
  isTwoObjectsEqual,
  removeArrayElementById,
  removeTimezoneOffset,
} from '@/utils/index.js';
import { disableButton } from '@/mixins/disableButton.js';
import { GET_DESCRIPTION } from '@/store/mutationTypes.js';

import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';

export default {
  name: 'EditSkills',
  props: {
    skillsData: {
      type: Array,
      required: false,
    },
    activityCVId: {
      type: Number,
      required: true,
    },
    activityId: {
      type: Number,
      required: true,
    },
  },
  mixins: [disableButton],
  components: {
    ConfirmationDialog,
  },
  data() {
    return {
      skillArray: [],
      skillTypeDescriptions: null,
      skillFormRules: {
        level: [
          {
            required: true,
            message: '',
            trigger: 'blur',
          },
        ],
        skillTypeId: [
          {
            required: false,
            message: '',
            trigger: 'change',
          },
        ],
        otherSkillType: [
          {
            required: false,
            message: '',
            trigger: 'blur',
          },
        ],
      },
      isSkillsVisible: false,
    };
  },
  computed: {
    descriptions() {
      return this.$store.state.descriptions;
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    isTwoObjectsEqual,
    removeArrayElementById,
    removeTimezoneOffset,
    async fetchSkillTypeDescriptions() {
      const { activityId } = this;
      const { success, data } = await getSkillTypeDescriptions({
        activityId,
      });
      if (success) {
        this.skillTypeDescriptions = data.items;
      }
    },
    async requestAddSkill(skill) {
      this.disableButton('save-skill');
      const { activityCVId, removeTimezoneOffset } = this;
      const { level, skillTypeId, otherSkillType } = skill;

      const { success, errors } = await addSkill({
        activityCVId,
        level,
        skillTypeId,
        otherSkillType,
      });
      this.enableButton('save-skill');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Skill has been added',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setSkillErrors(skill, this.parseErrorsByFields(errors));
      }
    },
    async requestUpdateSkill(skill) {
      this.disableButton('save-skill');
      const { removeTimezoneOffset } = this;
      const { skillId, level, skillTypeId, otherSkillType } = skill;

      const { success, errors } = await updateSkill({
        skillId,
        level,
        skillTypeId,
        otherSkillType,
      });
      this.enableButton('save-skill');

      if (success) {
        this.showMessage({
          type: ' success',
          message: 'Skill has been updated',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setSkillErrors(skill, this.parseErrorsByFields(errors));
      }
    },
    async requestRemoveSkill(skill) {
      this.disableButton('delete-skill');
      const { skillId } = skill;
      const { success, errors } = await removeSkill({ skillId });
      this.enableButton('delete-skill');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Skill has been removed',
        });
        this.$emit('update-user-data');
      } else if (errors) {
        this.setSkillErrors(skill, this.parseErrorsByFields(errors));
      }
    },
    setSkillErrors(skill, errors) {
      const newErrors = {};
      for (const key in errors) {
        newErrors[key + 'Error'] = errors[key];
      }

      const skillIndex = this.skillArray.indexOf(skill);
      this.skillArray[skillIndex] = {
        ...skill,
        ...newErrors,
      };
    },
    setSkillsData() {
      const { skillsData } = this;

      this.skillArray = skillsData.map((skill) => {
        return {
          skillId: skill.skillId,
          level: skill.level,
          skillTypeId: skill.skillType ? skill.skillType.skillTypeId : null,
          otherSkillType: skill.otherSkillType,
          // errors for each field
          skillIdError: null,
          levelError: null,
          skillTypeIdError: null,
          otherSkillTypeError: null,
        };
      });
    },
    addSkill() {
      this.skillArray.push({
        skillId: null,
        level: null,
        skillTypeId: null,
        otherSkillType: null,
        // errors for each field
        skillIdError: null,
        levelError: null,
        skillTypeIdError: null,
        otherSkillTypeError: null,
      });
    },
    deleteSkill(skill) {
      const { skillArray, removeArrayElementById } = this;

      const skillIndex = skillArray.indexOf(skill);
      this.skillArray = removeArrayElementById(skillArray, skillIndex);

      if (skill.skillId) {
        this.requestRemoveSkill(skill);
      }
    },
    validateSkill(skill, index) {
      const {
        skillsData,
        isSkillFormFilled,
        removeTimezoneOffset,
        isTwoObjectsEqual,
      } = this;
      this.$refs.skill[index].validate();

      if (isSkillFormFilled(skill)) {
        const { skillId } = skill;

        if (skillId) {
          const addedSkill = skillsData.find(
            (skillItem) => skillItem.skillId === skillId,
          );

          const oldData = {
            skillId: addedSkill.skillId,
            level: addedSkill.level,
            skillTypeId: addedSkill.skillType.skillTypeId,
            otherSkillType: addedSkill.otherSkillType,
          };

          const newData = {
            skillId: skill.skillId,
            level: skill.level,
            skillTypeId: skill.skillTypeId,
            otherSkillType: skill.otherSkillType,
          };

          if (isTwoObjectsEqual(newData, oldData)) {
            this.showMessage({
              type: 'error',
              message: 'There are no changes to be saved',
            });
          } else {
            this.requestUpdateSkill(skill);
          }
        } else {
          this.requestAddSkill(skill);
        }
      } else {
        const { skillTypeId, otherSkillType } = skill;
        if (!skillTypeId && !otherSkillType) {
          skill.skillTypeIdError = 'Choose skill type or enter other type';
          skill.otherSkillTypeError = 'Choose skill type or enter other type';
        }
      }
    },
    isSkillFormFilled(skill) {
      const { level, skillTypeId, otherSkillType } = skill;
      const requiredFields = {
        level: skill.level,
        skillTypeId: skill.skillTypeId,
        otherSkillType: skill.otherSkillType,
      };

      return level !== null && (skillTypeId || otherSkillType);
    },
    toggleSkillsVisibility() {
      this.isSkillsVisible = !this.isSkillsVisible;
    },
  },
  created() {
    const { skillsData } = this;
    if (skillsData) {
      this.setSkillsData();
    }

    this.fetchSkillTypeDescriptions();

    if (this.descriptions.skillLevel.length === 0) {
      this.$store.dispatch(GET_DESCRIPTION, { key: 'skillLevel' });
    }

    this.isSkillsVisible = this.skillArray.length === 0;
  },
  watch: {
    skillsData(newVal) {
      if (newVal) {
        this.setSkillsData();
      }
    },
  },
};
</script>
